
export default function Privacy(){
  return (
    <div className="container py-12 prose max-w-4xl">
      <h1>Privacy Policy</h1>
      <p className="text-sm text-gray-600">Effective Date: January 1, 2025 | Last Updated: January 1, 2025</p>

      <h2>1. Introduction</h2>
      <p>
        AdKarrot LLC ("Company," "we," "us," or "our") respects your privacy and is committed to protecting
        your personal data. This Privacy Policy explains how we collect, use, share, and protect your
        information when you visit our website at www.adkarrot.com, engage our digital marketing services,
        or communicate with us.
      </p>
      <p>This policy complies with:</p>
      <ul>
        <li>Iowa Consumer Data Protection Act (ICDPA)</li>
        <li>California Consumer Privacy Act (CCPA/CPRA)</li>
        <li>General Data Protection Regulation (GDPR) for EU clients</li>
      </ul>

      <h2>2. Information We Collect</h2>

      <h3>2.1 Information You Provide Directly</h3>
      <p>We collect information you provide when you:</p>
      <ul>
        <li><strong>Contact us:</strong> Name, email address, phone number, business name, message content</li>
        <li><strong>Purchase services:</strong> Billing information (processed by Stripe), business details, service preferences</li>
        <li><strong>Use our services:</strong> Access credentials to your marketing accounts, website analytics, business goals and objectives</li>
        <li><strong>Subscribe to communications:</strong> Email address, name, communication preferences</li>
      </ul>

      <h3>2.2 Information Collected Automatically</h3>
      <p>When you visit our website, we automatically collect:</p>
      <ul>
        <li><strong>Device information:</strong> IP address, browser type, operating system, device identifiers</li>
        <li><strong>Usage data:</strong> Pages visited, time spent on pages, navigation paths, referral sources</li>
        <li><strong>Location data:</strong> General location based on IP address (city/state level)</li>
        <li><strong>Cookies and tracking:</strong> See our <a href="/legal/cookies">Cookie Policy</a> for details</li>
      </ul>

      <h3>2.3 Information from Third Parties</h3>
      <p>We may receive information from:</p>
      <ul>
        <li><strong>Stripe:</strong> Payment confirmation and transaction verification (we do NOT receive or store full credit card numbers)</li>
        <li><strong>Marketing platforms:</strong> Campaign performance data from Google Ads, Facebook Ads, LinkedIn when managing your accounts</li>
        <li><strong>Public sources:</strong> Publicly available business information and directories</li>
      </ul>

      <h2>3. How We Use Your Information</h2>
      <p>We use your information for the following purposes:</p>

      <h3>3.1 To Provide Services (Contractual Necessity)</h3>
      <ul>
        <li>Deliver the digital marketing services you've purchased</li>
        <li>Process payments and manage subscriptions</li>
        <li>Communicate about services, deliverables, and invoices</li>
        <li>Provide customer support and respond to inquiries</li>
        <li>Manage your marketing accounts and campaigns</li>
      </ul>

      <h3>3.2 For Business Operations (Legitimate Interest)</h3>
      <ul>
        <li>Improve our website and services</li>
        <li>Conduct analytics and research</li>
        <li>Train staff and develop new offerings</li>
        <li>Maintain security and prevent fraud</li>
        <li>Comply with legal obligations</li>
      </ul>

      <h3>3.3 For Marketing (With Consent Where Required)</h3>
      <ul>
        <li>Send promotional emails about our services (you may opt out anytime)</li>
        <li>Share case studies, educational content, and industry insights</li>
        <li>Display relevant advertising on third-party platforms</li>
      </ul>

      <h3>3.4 With Your Consent</h3>
      <ul>
        <li>Display your business as a client reference or in case studies</li>
        <li>Share project details in our portfolio</li>
        <li>Use performance metrics publicly (in anonymized form unless you consent otherwise)</li>
      </ul>

      <h2>4. How We Share Your Information</h2>

      <h3>4.1 We Do NOT Sell Your Personal Information</h3>
      <p className="font-bold">
        AdKarrot LLC does NOT sell your personal information for monetary consideration.
      </p>

      <h3>4.2 Service Providers</h3>
      <p>We share information with trusted service providers who perform services on our behalf:</p>
      <ul>
        <li><strong>Stripe:</strong> Payment processing (PCI DSS Level 1 certified)</li>
        <li><strong>Google:</strong> Analytics, email (Google Workspace), cloud storage</li>
        <li><strong>Marketing platforms:</strong> When managing campaigns on your behalf (Google Ads, Facebook, LinkedIn)</li>
      </ul>
      <p>
        These providers are contractually obligated to use data only for specified purposes, maintain
        appropriate security measures, and protect your information.
      </p>

      <h3>4.3 Advertising Partners (May Constitute "Sharing" Under CCPA)</h3>
      <p>
        We may share identifiers and website activity with advertising partners for retargeting and
        analytics purposes. This may constitute "sharing" under California law. California residents
        can opt out (see Your Privacy Rights below).
      </p>

      <h3>4.4 Legal Requirements</h3>
      <p>We may disclose information when required by law, court order, or to:</p>
      <ul>
        <li>Protect our legal rights and enforce our Terms of Service</li>
        <li>Prevent fraud or security threats</li>
        <li>Protect the safety of individuals</li>
        <li>Comply with tax and regulatory obligations</li>
      </ul>

      <h3>4.5 Business Transfers</h3>
      <p>
        If AdKarrot LLC is acquired, merges with another company, or sells assets, your information
        may be transferred as part of that transaction.
      </p>

      <h2>5. Data Retention</h2>
      <p>We retain your personal information as follows:</p>
      <ul>
        <li><strong>Active client data:</strong> Duration of service relationship + 5 years</li>
        <li><strong>Financial records:</strong> 7 years (IRS requirement)</li>
        <li><strong>Marketing data:</strong> Until you opt out + 30 days</li>
        <li><strong>Website analytics:</strong> 26 months (Google Analytics default)</li>
        <li><strong>Contract documents:</strong> Duration + 7 years (statute of limitations)</li>
      </ul>
      <p>
        After retention periods expire, we securely delete or anonymize data unless longer retention
        is required by law.
      </p>

      <h2>6. Your Privacy Rights</h2>

      <h3>6.1 All Users</h3>
      <p>You have the right to:</p>
      <ul>
        <li><strong>Access:</strong> Request a copy of your personal data</li>
        <li><strong>Correction:</strong> Request correction of inaccurate information</li>
        <li><strong>Deletion:</strong> Request deletion of your personal data (subject to legal retention requirements)</li>
        <li><strong>Opt-out of marketing:</strong> Unsubscribe from promotional emails at any time</li>
      </ul>

      <h3>6.2 California Residents (CCPA/CPRA Rights)</h3>
      <p>If you are a California resident, you have additional rights:</p>
      <ul>
        <li><strong>Know:</strong> Request disclosure of personal information collected, used, shared, or sold in the past 12 months</li>
        <li><strong>Data portability:</strong> Obtain a copy of your data in portable format</li>
        <li><strong>Opt out of sale/sharing:</strong> Opt out of sale or sharing of personal information (we don't sell, but may share for advertising)</li>
        <li><strong>Non-discrimination:</strong> We will not discriminate against you for exercising your rights</li>
      </ul>
      <p>
        <a href="mailto:privacy@adkarrot.com?subject=Do%20Not%20Sell%20or%20Share%20My%20Information" className="font-bold">
          Do Not Sell or Share My Personal Information
        </a>
      </p>

      <h3>6.3 Iowa Residents (ICDPA Rights)</h3>
      <p>Iowa residents have the right to:</p>
      <ul>
        <li>Confirm whether we process your personal data and access that data</li>
        <li>Delete your personal data</li>
        <li>Obtain a copy of your data in portable format</li>
        <li>Opt out of the sale of personal data (we don't sell)</li>
      </ul>

      <h3>6.4 EU Residents (GDPR Rights)</h3>
      <p>If you are in the European Union or UK, you have the right to:</p>
      <ul>
        <li>Access, rectification, and erasure of your data</li>
        <li>Restriction of processing and data portability</li>
        <li>Object to processing based on legitimate interests</li>
        <li>Withdraw consent where processing is based on consent</li>
        <li>Lodge a complaint with your local data protection authority</li>
      </ul>

      <h3>6.5 How to Exercise Your Rights</h3>
      <p>To exercise your privacy rights:</p>
      <ul>
        <li><strong>Email:</strong> <a href="mailto:privacy@adkarrot.com">privacy@adkarrot.com</a></li>
        <li><strong>Phone:</strong> 319-343-6094</li>
        <li><strong>Mail:</strong> AdKarrot LLC, Attn: Privacy Officer, 309 Court Avenue Suite 855, Des Moines, IA 50309</li>
      </ul>
      <p>
        We will verify your identity and respond within 45 days (Iowa/California) or 30 days (GDPR).
        We do not charge fees for reasonable requests.
      </p>

      <h2>7. Cookies and Tracking Technologies</h2>
      <p>
        We use cookies and similar technologies to improve website functionality, analyze usage, and
        deliver relevant advertising. For detailed information, please see our <a href="/legal/cookies">Cookie Policy</a>.
      </p>
      <p><strong>Key points:</strong></p>
      <ul>
        <li>Essential cookies: Required for website functionality</li>
        <li>Analytics cookies: Google Analytics for usage statistics (you can opt out)</li>
        <li>Marketing cookies: Advertising and retargeting (you can opt out)</li>
      </ul>
      <p>Opt-out options:</p>
      <ul>
        <li>Cookie preferences on our website</li>
        <li>Browser settings to block cookies</li>
        <li><a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-Out</a></li>
        <li><a href="https://optout.aboutads.info" target="_blank" rel="noopener noreferrer">Digital Advertising Alliance Opt-Out</a></li>
      </ul>

      <h2>8. Data Security</h2>
      <p>We implement reasonable security measures to protect your information:</p>

      <h3>8.1 Technical Measures</h3>
      <ul>
        <li>SSL/TLS encryption for data transmission</li>
        <li>Encrypted storage of sensitive data</li>
        <li>Regular security patches and updates</li>
        <li>Access controls and authentication</li>
      </ul>

      <h3>8.2 Payment Security</h3>
      <p className="font-bold">
        We do NOT store credit card information. Stripe (PCI DSS Level 1 certified) handles all
        payment card data securely. We never have access to your full credit card number.
      </p>

      <h3>8.3 Limitations</h3>
      <p>
        No security system is 100% effective. While we implement industry-standard protections, we
        cannot guarantee absolute security. In the event of a data breach, we will notify affected
        individuals as required by law.
      </p>

      <h2>9. Children's Privacy</h2>
      <p>
        Our services are directed to businesses, not children. We do not knowingly collect information
        from individuals under 16 years of age. If we discover we have collected information from a
        child under 16, we will delete it promptly. Parents or guardians who believe we have collected
        child data should contact us immediately.
      </p>

      <h2>10. International Data Transfers</h2>
      <p>
        AdKarrot LLC is based in the United States. If you are located outside the U.S., your information
        will be transferred to and processed in the United States. The U.S. may not have the same data
        protection laws as your jurisdiction. We take steps to ensure adequate protection through
        contractual safeguards with service providers.
      </p>

      <h2>11. Third-Party Links</h2>
      <p>
        Our website may contain links to third-party websites. We are not responsible for the privacy
        practices of those websites. We encourage you to read their privacy policies.
      </p>

      <h2>12. Changes to This Privacy Policy</h2>
      <p>
        We may update this Privacy Policy periodically to reflect changes in our practices, services,
        or legal requirements. We will notify you of material changes via email (if you are a client
        or subscriber) with 30 days advance notice. The "Last Updated" date at the top indicates when
        the policy was last revised.
      </p>
      <p>Your continued use of our services after changes indicates acceptance.</p>

      <h2>13. Contact Us</h2>
      <p>For privacy-related questions, concerns, or to exercise your rights:</p>
      <p>
        <strong>Privacy Inquiries:</strong><br/>
        Email: <a href="mailto:privacy@adkarrot.com">privacy@adkarrot.com</a><br/>
        Phone: 319-343-6094<br/>
        Mail: AdKarrot LLC, Attn: Privacy Officer, 309 Court Avenue Suite 855, Des Moines, IA 50309
      </p>
      <p>
        <strong>General Inquiries:</strong><br/>
        Email: <a href="mailto:support@adkarrot.com">support@adkarrot.com</a><br/>
        Phone: 319-343-6094
      </p>

      <div className="mt-8 p-4 bg-gray-50 border border-gray-200 rounded">
        <p className="text-sm font-semibold">California Residents:</p>
        <p className="text-sm">
          <a href="mailto:privacy@adkarrot.com?subject=Do%20Not%20Sell%20or%20Share%20My%20Information" className="text-blue-600 hover:underline">
            Do Not Sell or Share My Personal Information
          </a>
        </p>
      </div>
    </div>
  )
}
